import { lazy, Suspense, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calculator, MapPin, Plus, Send, Trash2 } from 'lucide-react';
import { CITY, PARCEL_SIZE_LABELS, ParcelSize, type LatLng } from '@kick/types';
import { createMerchantBatchSchema } from '@kick/validation';
import { getErrorMessage } from '@/lib/api';
import { cn, formatCOP, formatDistance } from '@/lib/cn';
import { textoUbicacion, useUbicacionAutomatica } from '@/lib/geocodificar';
import { useCotizarLote, useCrearLote, useMerchantId, type CotizacionLote } from '@/lib/queries';
import { AreaTexto, Boton, Campo, Entrada, Seleccion } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

const SelectorPunto = lazy(() =>
  import('@/components/SelectorPunto').then((m) => ({ default: m.SelectorPunto })),
);

interface FilaEnvio {
  key: number;
  direccion: string;
  detalles: string;
  recibe: string;
  telefono: string;
  descripcion: string;
  punto: LatLng | null;
}

let contador = 1;
const filaVacia = (): FilaEnvio => ({
  key: contador++,
  direccion: '',
  detalles: '',
  recibe: '',
  telefono: '',
  descripcion: '',
  punto: null,
});

/**
 * Envíos masivos: el comercio arma la lista y administración reparte.
 *
 * Una tienda que saca veinte paquetes no quiere veinte formularios. Aquí
 * cada renglón es un envío: la dirección se ubica sola en el mapa al
 * escribirla (y se puede ajustar), se cotiza todo de una vez y se confirma
 * el lote entero. Quién lo lleva no lo decide el motor: lo decide alguien de
 * administración mirando la lista, a uno o a varios domiciliarios.
 */
export function EnviosMasivos() {
  const merchantId = useMerchantId();
  const navigate = useNavigate();
  const avisos = useAvisos();
  const cotizar = useCotizarLote(merchantId);
  const crear = useCrearLote(merchantId);

  const [filas, setFilas] = useState<FilaEnvio[]>([filaVacia(), filaVacia()]);
  const [size, setSize] = useState<ParcelSize>(ParcelSize.MEDIUM);
  const [dondeRecoger, setDondeRecoger] = useState('');
  const [notas, setNotas] = useState('');
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [cotizacion, setCotizacion] = useState<CotizacionLote | null>(null);

  const cambiar = (key: number, cambios: Partial<FilaEnvio>): void => {
    setFilas((f) => f.map((x) => (x.key === key ? { ...x, ...cambios } : x)));
    // Cualquier cambio invalida el precio: hay que volver a cotizar.
    setCotizacion(null);
  };

  const quitar = (key: number): void => {
    setFilas((f) => (f.length > 1 ? f.filter((x) => x.key !== key) : f));
    setCotizacion(null);
  };

  const armar = () => ({
    items: filas.map((f) => ({
      dropoff: {
        addressLine: f.direccion.trim(),
        ...(f.detalles.trim() ? { details: f.detalles.trim() } : {}),
        location: f.punto ?? { ...CITY.center },
      },
      recipientName: f.recibe.trim(),
      recipientPhone: f.telefono.trim(),
      description: f.descripcion.trim(),
    })),
    size,
    ...(dondeRecoger.trim() ? { pickupDetails: dondeRecoger.trim() } : {}),
    ...(notas.trim() ? { notes: notas.trim() } : {}),
  });

  /** Valida con el mismo esquema del servidor y señala el renglón y el campo. */
  const validar = (): boolean => {
    const sinPunto = filas.findIndex((f) => !f.punto);
    const revision = createMerchantBatchSchema.safeParse(armar());
    const encontrados: Record<string, string> = {};
    if (!revision.success) {
      for (const problema of revision.error.issues) {
        encontrados[problema.path.join('.')] ??= problema.message;
      }
    }
    if (sinPunto >= 0) {
      encontrados[`items.${sinPunto}.dropoff.location`] =
        'Esa dirección no se ubicó en el mapa: escríbela mejor o ajústala a mano';
    }
    setErrores(encontrados);
    return Object.keys(encontrados).length === 0;
  };

  const calcular = (): void => {
    if (!validar()) return;
    cotizar.mutate(
      { items: filas.map((f) => ({ location: f.punto! })), size },
      {
        onSuccess: setCotizacion,
        onError: (e) => avisos.error(getErrorMessage(e)),
      },
    );
  };

  const confirmar = (): void => {
    if (!validar()) return;
    crear.mutate(armar(), {
      onSuccess: (r) => {
        avisos.exito(
          `Lote de ${r.items.length} envíos creado (${formatCOP(r.total)}). Administración asigna el domiciliario.`,
        );
        navigate('/envios');
      },
      onError: (e) => avisos.error(getErrorMessage(e)),
    });
  };

  const fueraDeCobertura = cotizacion?.items.some((i) => !i.covered) ?? false;

  return (
    <div className="mx-auto max-w-5xl">
      <Link
        to="/envios"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-kick-gray hover:text-kick-dark"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a envíos
      </Link>
      <h1 className="mt-2 text-3xl text-kick-dark">Envíos masivos</h1>
      <p className="mt-1 text-sm text-kick-gray">
        Arma la lista de todo lo que sale del local. Cada envío se paga al domiciliario cuando
        recoge, como siempre; nuestro equipo decide qué domiciliario (o domiciliarios) lo lleva y
        te avisamos.
      </p>

      <section className="card-kick mt-6 grid gap-4 p-5 sm:grid-cols-3">
        <Campo etiqueta="Tamaño de los paquetes" ayuda="Se aplica a todos los envíos del lote.">
          <Seleccion value={size} onChange={(e) => setSize(e.target.value as ParcelSize)}>
            {Object.values(ParcelSize).map((s) => (
              <option key={s} value={s}>
                {PARCEL_SIZE_LABELS[s]}
              </option>
            ))}
          </Seleccion>
        </Campo>
        <Campo etiqueta="Dónde recogerlos dentro del local" ayuda="Opcional.">
          <Entrada
            value={dondeRecoger}
            onChange={(e) => setDondeRecoger(e.target.value)}
            maxLength={150}
            placeholder="Ej: En la bodega, pregunta por Luis"
          />
        </Campo>
        <Campo etiqueta="Nota para el domiciliario" ayuda="Opcional, para todo el lote.">
          <Entrada
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            maxLength={300}
            placeholder="Ej: Todos son frágiles"
          />
        </Campo>
      </section>

      <div className="mt-6 space-y-4">
        {filas.map((fila, i) => (
          <FilaEditor
            key={fila.key}
            numero={i + 1}
            fila={fila}
            errores={errores}
            indice={i}
            precio={cotizacion?.items[i] ?? null}
            onCambio={(cambios) => cambiar(fila.key, cambios)}
            onQuitar={filas.length > 1 ? () => quitar(fila.key) : undefined}
          />
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <Boton
          variante="secundario"
          icono={<Plus className="h-4 w-4" />}
          onClick={() => setFilas((f) => [...f, filaVacia()])}
          disabled={filas.length >= 60}
        >
          Agregar envío
        </Boton>
        <div className="flex flex-wrap items-center gap-3">
          {cotizacion && !fueraDeCobertura && (
            <p className="text-sm text-kick-ink">
              {filas.length} envíos · <strong>{formatCOP(cotizacion.total)}</strong> al recoger
            </p>
          )}
          <Boton
            variante="secundario"
            icono={<Calculator className="h-4 w-4" />}
            cargando={cotizar.isPending}
            onClick={calcular}
          >
            Calcular precios
          </Boton>
          <Boton
            icono={<Send className="h-4 w-4" />}
            cargando={crear.isPending}
            disabled={!cotizacion || fueraDeCobertura}
            onClick={confirmar}
          >
            Confirmar lote
          </Boton>
        </div>
      </div>
      {fueraDeCobertura && (
        <p className="mt-2 text-right text-sm text-red-600">
          Hay direcciones fuera de cobertura: corrígelas antes de confirmar.
        </p>
      )}
    </div>
  );
}

function FilaEditor({
  numero,
  fila,
  errores,
  indice,
  precio,
  onCambio,
  onQuitar,
}: {
  numero: number;
  fila: FilaEnvio;
  errores: Record<string, string>;
  indice: number;
  precio: CotizacionLote['items'][number] | null;
  onCambio: (cambios: Partial<FilaEnvio>) => void;
  onQuitar?: () => void;
}) {
  const [verMapa, setVerMapa] = useState(false);
  const ubicacion = useUbicacionAutomatica(
    fila.direccion,
    (punto) => onCambio({ punto }),
    fila.punto ?? CITY.center,
  );
  const estadoMapa = textoUbicacion(ubicacion);
  const e = (campo: string): string | undefined => errores[`items.${indice}.${campo}`];

  return (
    <section
      role="region"
      className={cn(
        'card-kick p-5',
        precio && !precio.covered ? 'border-red-300' : '',
      )}
      aria-label={`Envío ${numero}`}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg text-kick-dark">Envío {numero}</h2>
        <div className="flex items-center gap-3">
          {precio && (
            <span
              className={cn(
                'text-sm font-semibold',
                precio.covered ? 'text-kick-ink' : 'text-red-600',
              )}
            >
              {precio.covered
                ? `${formatDistance(precio.distanceMeters)} · ${formatCOP(precio.total)}`
                : 'Fuera de cobertura'}
            </span>
          )}
          {onQuitar && (
            <button
              type="button"
              onClick={onQuitar}
              className="rounded-kick p-1.5 text-kick-gray hover:bg-red-50 hover:text-red-600"
              aria-label={`Quitar envío ${numero}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          {/* Solo el control dentro de Campo: así la etiqueta queda unida al input. */}
          <Campo
            etiqueta="Dirección de entrega"
            requerido
            error={e('dropoff.addressLine') ?? e('dropoff.location')}
          >
            <Entrada
              value={fila.direccion}
              onChange={(ev) => onCambio({ direccion: ev.target.value })}
              maxLength={250}
              placeholder="Ej: Calle 10 #43-25, El Poblado"
              error={!!(e('dropoff.addressLine') ?? e('dropoff.location'))}
            />
          </Campo>
          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
            <p
              className={cn(
                'text-xs',
                estadoMapa.tono === 'ok'
                  ? 'text-emerald-700'
                  : estadoMapa.tono === 'aviso'
                    ? 'text-amber-700'
                    : 'text-kick-gray',
              )}
            >
              {estadoMapa.texto}
            </p>
            <button
              type="button"
              onClick={() => setVerMapa((v) => !v)}
              className="flex items-center gap-1 text-xs font-semibold text-kick-red hover:underline"
            >
              <MapPin className="h-3.5 w-3.5" />
              {verMapa ? 'Ocultar mapa' : 'Ajustar en el mapa'}
            </button>
          </div>
          {verMapa && (
            <div className="mt-2">
              <Suspense fallback={<div className="h-56 animate-pulse rounded-kick bg-kick-gray/10" />}>
                <SelectorPunto valor={fila.punto} onCambio={(punto) => onCambio({ punto })} altura="h-56" />
              </Suspense>
            </div>
          )}
        </div>

        <Campo etiqueta="Detalles" ayuda="Torre, apartamento, referencia.">
          <Entrada
            value={fila.detalles}
            onChange={(ev) => onCambio({ detalles: ev.target.value })}
            maxLength={150}
            placeholder="Ej: Torre B, apto 502"
          />
        </Campo>
        <Campo etiqueta="Qué va" requerido error={e('description')}>
          <Entrada
            value={fila.descripcion}
            onChange={(ev) => onCambio({ descripcion: ev.target.value })}
            maxLength={300}
            placeholder="Ej: Pedido #123, 2 cajas"
            error={!!e('description')}
          />
        </Campo>
        <Campo etiqueta="Quién recibe" requerido error={e('recipientName')}>
          <Entrada
            value={fila.recibe}
            onChange={(ev) => onCambio({ recibe: ev.target.value })}
            maxLength={140}
            error={!!e('recipientName')}
          />
        </Campo>
        <Campo etiqueta="Su teléfono" requerido error={e('recipientPhone')}>
          <Entrada
            value={fila.telefono}
            onChange={(ev) => onCambio({ telefono: ev.target.value })}
            inputMode="tel"
            maxLength={20}
            placeholder="3001234567"
            error={!!e('recipientPhone')}
          />
        </Campo>
      </div>
    </section>
  );
}

// El textarea queda disponible para notas largas si el comercio las necesita.
export { AreaTexto as _AreaTextoLote };
